export const DEV_TOOLS_POSITION_KEY = 'imai-dev-tools-position';

export const DEV_CONSOLE_SIZE = { width: 260, height: 112 } as const;

export type DevToolsPoint = { x: number; y: number };

type DevToolsSize = { width: number; height: number };

const CHORD_KEYS = new Set(['d', 'e', 'v']);

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * Local `next dev`, and the long-lived dev deployment (`IMAI_ENV=dev`).
 * Preview, beta, and production stay closed.
 */
export function isDevToolsDeployment(input: {
  nodeEnv: string | undefined;
  imaiEnv: string | undefined;
}): boolean {
  if (input.nodeEnv === 'development') return true;
  const env = input.imaiEnv?.trim().toLowerCase();
  return env === 'dev' || env === 'development';
}

export function shouldIgnoreDevChordTarget(
  target: { tagName: string; isContentEditable: boolean } | null
): boolean {
  if (!target) return false;
  if (target.isContentEditable) return true;
  return EDITABLE_TAGS.has(target.tagName);
}

function chordKey(event: { key: string; code?: string }): string | null {
  if (event.code === 'KeyD') return 'd';
  if (event.code === 'KeyE') return 'e';
  if (event.code === 'KeyV') return 'v';
  const key = event.key.toLowerCase();
  return CHORD_KEYS.has(key) ? key : null;
}

/** Holding D, E, and V together toggles the console. Repeats do not. */
export function applyDevChordKey(
  held: ReadonlySet<string>,
  event: { type: 'keydown' | 'keyup'; key: string; code?: string }
): { held: ReadonlySet<string>; toggled: boolean } {
  const key = chordKey(event);
  if (!key) return { held, toggled: false };
  if (event.type === 'keydown' && held.has(key)) {
    return { held, toggled: false };
  }

  const next = new Set(held);
  if (event.type === 'keydown') next.add(key);
  else next.delete(key);

  return {
    held: next,
    toggled: event.type === 'keydown' && next.size === CHORD_KEYS.size,
  };
}

export function clampDevToolsPosition(
  point: DevToolsPoint,
  consoleSize: DevToolsSize,
  viewport: DevToolsSize
): DevToolsPoint {
  const maxX = Math.max(0, viewport.width - consoleSize.width);
  const maxY = Math.max(0, viewport.height - consoleSize.height);
  return {
    x: Math.min(maxX, Math.max(0, point.x)),
    y: Math.min(maxY, Math.max(0, point.y)),
  };
}

export function initialDevToolsPosition(
  viewport: DevToolsSize,
  consoleSize: DevToolsSize = DEV_CONSOLE_SIZE
): DevToolsPoint {
  return clampDevToolsPosition(
    { x: (viewport.width - consoleSize.width) / 2, y: 8 },
    consoleSize,
    viewport
  );
}

export function moveDevConsole(input: {
  start: DevToolsPoint;
  pointerStart: DevToolsPoint;
  pointer: DevToolsPoint;
  consoleSize: DevToolsSize;
  viewport: DevToolsSize;
}): DevToolsPoint {
  return clampDevToolsPosition(
    {
      x: input.start.x + input.pointer.x - input.pointerStart.x,
      y: input.start.y + input.pointer.y - input.pointerStart.y,
    },
    input.consoleSize,
    input.viewport
  );
}

export function parseDevToolsPosition(
  value: string | null
): DevToolsPoint | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    const point = parsed as { x?: unknown; y?: unknown };
    if (typeof point.x !== 'number' || typeof point.y !== 'number') return null;
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;
    return { x: point.x, y: point.y };
  } catch {
    return null;
  }
}
